import { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2,
  MessageSquareText,
  Pause,
  Play,
  RotateCcw,
  ScanText,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  Trash2,
  Wifi,
  WifiOff,
} from "lucide-react";
import { api, type Camera, type Scope } from "../api";
import { PageHeader } from "../components/PageHeader";
import { useScope } from "../components/ScopeContext";
import { ZoomableStillDialog } from "../components/ZoomableStillDialog";

// Dev-only live viewer: watch crops flow to the captioner and captions land
// back, keyed by corr_id. Backed by the WebSocket at
// /api/dev/caption-feed/ws (services/api/.../routes/dev_captions.py). The
// crop image is captured server-side the moment it hits the `crops` stream,
// before the captioner deletes the JPEG post-caption.
//
// This is a pure debug surface: it stores NOTHING. The socket is only open
// while the page is visible and not paused, so it does no background work
// when you navigate away, switch tabs, or pause.

const MAX_ITEMS = 100;
const RECONNECT_MS = 1500;

type Verdict = "confirmed" | "cleared";

type FeedItem = {
  corr_id: string;
  camera_id?: string;
  // String, not number: tracker ids are 64-bit and exceed JS's 2**53 safe
  // integer range, so parsing them as numbers rounds the last digits.
  track_id?: string | null;
  conf?: number | null;
  acquired_at_utc?: string | null;
  image_b64?: string | null;
  frame_b64?: string | null;
  caption?: string | null;
  vlm_raw?: string | null;
  activity?: string | null;
  caption_model?: string | null;
  escalated?: boolean;
  verdict?: Verdict | null;
  verify_answer?: string | null;
  crop_ts_ms?: number;
  caption_ts_ms?: number;
  // On-demand crop ask — client-side only, never persisted.
  verifying?: boolean;
  manual_answer?: string | null;
  manual_confirmed?: boolean | null;
  manual_standalone?: boolean;
  manual_truncated?: boolean;
};

type ProbeResult = {
  corr_id: string;
  image_b64: string;
  prompt: string;
  answer: string;
  standalone: boolean;
  confirmed: boolean | null;
  truncated: boolean;
};

type FeedEvent =
  | ({ type: "crop" } & FeedItem & { ts_ms: number })
  | ({ type: "caption" } & FeedItem & { ts_ms: number })
  | { type: "escalation"; corr_id: string; camera_id?: string; ts_ms: number }
  | {
      type: "verdict";
      corr_id: string;
      camera_id?: string;
      status: Verdict;
      verify_answer?: string | null;
      ts_ms: number;
    };

type Conn = "connecting" | "live" | "closed";

function wsUrl(path: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}${path}`;
}

function captionFeedPath(scope?: Scope): string {
  const q = new URLSearchParams();
  if (scope?.store) q.set("scope_store", scope.store);
  if (scope?.profileId) q.set("scope_profile", scope.profileId);
  const qs = q.toString();
  return `/api/dev/caption-feed/ws${qs ? `?${qs}` : ""}`;
}

function jpegSrc(b64?: string | null): string | undefined {
  if (!b64) return undefined;
  return b64.startsWith("data:") ? b64 : `data:image/jpeg;base64,${b64}`;
}

function cameraHeading(
  cameraId: string | undefined,
  byId: Map<string, Camera>,
): { name: string; store: string } {
  if (!cameraId) return { name: "cam ?", store: "" };
  const cam = byId.get(cameraId);
  if (!cam) return { name: `cam ${cameraId}`, store: "" };
  const name = (cam.name || "").trim() || cameraId;
  return { name, store: (cam.store || "").trim() };
}

const CaptionCard = memo(function CaptionCard({
  item,
  onVerify,
  onOpen,
  cameraName,
  storeName,
  askMode,
}: {
  item: FeedItem;
  onVerify: (corrId: string) => void;
  onOpen: (item: FeedItem) => void;
  cameraName: string;
  storeName?: string;
  askMode: boolean;
}) {
  const raw = (item.vlm_raw || item.caption || "").trim();
  const waiting = !raw;
  const cropGone = !item.image_b64;
  const latencyMs =
    item.crop_ts_ms && item.caption_ts_ms
      ? item.caption_ts_ms - item.crop_ts_ms
      : null;

  return (
    <div
      className={
        "bf-card flex flex-col overflow-hidden " +
        (item.verdict === "confirmed" || item.manual_confirmed
          ? "ring-1 ring-red-500/60"
          : "")
      }
    >
      <div className="relative aspect-[3/4] w-full bg-black/40">
        {item.image_b64 ? (
          <button
            type="button"
            className="h-full w-full"
            onClick={() => onOpen(item)}
            aria-label="Open crop viewer"
          >
            <img
              src={jpegSrc(item.image_b64)}
              alt={`crop ${item.corr_id}`}
              className="h-full w-full cursor-zoom-in object-contain"
            />
          </button>
        ) : (
          <div className="flex h-full w-full items-center justify-center px-3 text-center text-xs text-bf-muted">
            {cropGone ? "crop expired before capture" : "no image"}
          </div>
        )}

        <div className="absolute left-1.5 top-1.5 flex flex-col items-start gap-1">
          {item.escalated && !item.verdict && (
            <span className="flex items-center gap-1 rounded bg-white/85 px-1.5 py-0.5 text-[10px] font-semibold text-black">
              <Loader2 size={11} className="animate-spin" /> verifying
            </span>
          )}
          {item.verdict === "confirmed" && (
            <span className="flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              <ShieldAlert size={11} /> confirmed
            </span>
          )}
          {item.verdict === "cleared" && (
            <span className="flex items-center gap-1 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              <ShieldCheck size={11} /> cleared
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-2.5">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-white/90">
            {cameraName}
            {storeName ? (
              <span className="text-bf-muted"> · {storeName}</span>
            ) : null}
            <span className="text-bf-muted"> · trk {item.track_id ?? "?"}</span>
          </span>
          <span className="tabular-nums text-bf-muted">
            {item.acquired_at_utc
              ? new Date(item.acquired_at_utc).toLocaleTimeString()
              : ""}
          </span>
        </div>

        {waiting ? (
          <div className="flex items-center gap-2 py-2 text-xs text-bf-muted">
            <Loader2 size={13} className="animate-spin" /> captioning…
          </div>
        ) : (
          <p className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words text-[11px] leading-snug text-white/85">
            {raw}
          </p>
        )}

        <div className="flex flex-col gap-1.5 border-t border-white/5 pt-2">
          <button
            type="button"
            disabled={!item.image_b64 || item.verifying}
            onClick={() => onVerify(item.corr_id)}
            className="flex items-center justify-center gap-1.5 rounded-md border border-white/10 py-1 text-[11px] font-medium text-white/80 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
            title={
              item.image_b64
                ? askMode
                  ? "Send the crop prompt with this image"
                  : "Run the weapon verify prompt on this crop"
                : "No crop image available"
            }
          >
            {item.verifying ? (
              <Loader2 size={12} className="animate-spin" />
            ) : askMode ? (
              <MessageSquareText size={12} />
            ) : (
              <ShieldQuestion size={12} />
            )}
            {askMode ? "Ask" : "Force 2nd pass"}
          </button>
          {item.manual_answer != null && !item.verifying && (
            item.manual_standalone ? (
              <div className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded bg-black/30 px-1.5 py-1 text-[11px] leading-snug text-white/85">
                {item.manual_answer}
                {item.manual_truncated ? (
                  <span className="mt-1 block text-[10px] text-amber-300/80">
                    Cut off at the token cap.
                  </span>
                ) : null}
              </div>
            ) : (
              <div
                className={
                  "whitespace-pre-wrap break-words rounded px-1.5 py-0.5 text-[10px] font-medium " +
                  (item.manual_confirmed
                    ? "bg-red-600/90 text-white"
                    : item.manual_confirmed === false
                      ? "bg-emerald-600/90 text-white"
                      : "bg-white/10 text-white/70")
                }
              >
                {item.manual_answer}
              </div>
            )
          )}
        </div>

        <div className="mt-auto flex items-center justify-between pt-1 text-[10px] text-bf-muted">
          <span className="truncate" title={item.corr_id}>
            {item.corr_id.slice(0, 8)}
          </span>
          {latencyMs != null && (
            <span className="tabular-nums">{(latencyMs / 1000).toFixed(1)}s</span>
          )}
          {item.verify_answer && (
            <span className="truncate italic" title={item.verify_answer}>
              “{item.verify_answer}”
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

export default function DevCaptions() {
  const { scope, scopeParams, scopeKey, scopeLabel } = useScope();
  const camerasQ = useQuery({
    queryKey: ["cameras", scopeKey],
    queryFn: () => api.cameras.list(scopeParams),
    staleTime: 60_000,
  });
  const camById = useMemo(() => {
    const m = new Map<string, Camera>();
    for (const c of camerasQ.data ?? []) m.set(c.id, c);
    return m;
  }, [camerasQ.data]);
  const allowedRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (scope.kind === "all") {
      allowedRef.current = null;
      return;
    }
    allowedRef.current = camerasQ.data
      ? new Set(camerasQ.data.map((c) => c.id))
      : null;
  }, [scope.kind, camerasQ.data]);

  const [items, setItems] = useState<FeedItem[]>([]);
  const [conn, setConn] = useState<Conn>("closed");
  const [paused, setPaused] = useState(false);
  const [total, setTotal] = useState(0);

  // Transient crop prompt (dev only, never persisted, lost on reload).
  // Blank = use the configured weapon_verify_prompt.
  const [showPrompt, setShowPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [loadingDefault, setLoadingDefault] = useState(false);
  const [lastProbe, setLastProbe] = useState<ProbeResult | null>(null);
  const [viewer, setViewer] = useState<FeedItem | null>(null);

  // Live state lives in refs so the socket's onmessage closure always sees
  // the latest data without re-subscribing.
  const mapRef = useRef<Map<string, FeedItem>>(new Map());
  const orderRef = useRef<string[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  // Prompt kept in a ref too, so `verify` stays referentially stable (no
  // re-render of every card on each keystroke).
  const promptRef = useRef("");
  useEffect(() => {
    promptRef.current = customPrompt;
  }, [customPrompt]);

  // The feed runs only while the page is mounted and not paused. Leaving the
  // page (route change) unmounts and tears the socket down, so it does no
  // background work when unused. NB: we intentionally do NOT gate on
  // document.hidden — embedded browsers (e.g. Cursor's) report the tab as
  // permanently "hidden", which would keep the socket from ever opening.
  const active = !paused;

  useEffect(() => {
    mapRef.current.clear();
    orderRef.current = [];
    setItems([]);
    setTotal(0);
  }, [scopeKey]);

  const applyEvent = useCallback((ev: FeedEvent) => {
    const id = ev.corr_id;
    if (!id) return;
    const allowed = allowedRef.current;
    if (allowed && ev.camera_id && !allowed.has(ev.camera_id)) return;
    const map = mapRef.current;
    const order = orderRef.current;
    const existing = map.get(id);
    const it: FeedItem = existing ?? { corr_id: id };
    if (!existing) map.set(id, it);

    if (ev.type === "crop") {
      if (ev.image_b64) it.image_b64 = ev.image_b64;
      if (ev.frame_b64) it.frame_b64 = ev.frame_b64;
      it.camera_id = ev.camera_id ?? it.camera_id;
      it.track_id = ev.track_id ?? it.track_id;
      it.conf = ev.conf;
      it.acquired_at_utc = ev.acquired_at_utc ?? it.acquired_at_utc;
      it.crop_ts_ms = ev.ts_ms;
    } else if (ev.type === "caption") {
      it.caption = ev.caption;
      it.vlm_raw = ev.vlm_raw;
      it.activity = ev.activity;
      it.caption_model = ev.caption_model;
      it.camera_id = ev.camera_id ?? it.camera_id;
      it.track_id = ev.track_id ?? it.track_id;
      it.caption_ts_ms = ev.ts_ms;
    } else if (ev.type === "escalation") {
      it.escalated = true;
      it.camera_id = ev.camera_id ?? it.camera_id;
    } else if (ev.type === "verdict") {
      it.escalated = true;
      it.verdict = ev.status;
      it.verify_answer = ev.verify_answer;
    }

    if (!existing) {
      order.push(id);
      while (order.length > MAX_ITEMS) {
        const dropped = order.shift();
        if (dropped) map.delete(dropped);
      }
    }
  }, []);

  const flush = useCallback(() => {
    setItems(orderRef.current.map((id) => ({ ...mapRef.current.get(id)! })));
  }, []);

  const patch = useCallback(
    (corrId: string, fields: Partial<FeedItem>) => {
      const it = mapRef.current.get(corrId);
      if (!it) return;
      Object.assign(it, fields);
      flush();
    },
    [flush],
  );

  const verify = useCallback(
    async (corrId: string) => {
      const it = mapRef.current.get(corrId);
      if (!it?.image_b64 || it.verifying) return;
      patch(corrId, { verifying: true });
      try {
        const prompt = promptRef.current.trim();
        const r = await api.dev.forceVerify(it.image_b64, prompt || undefined);
        patch(corrId, {
          verifying: false,
          manual_answer: r.answer,
          manual_confirmed: r.standalone ? null : r.confirmed,
          manual_standalone: r.standalone,
          manual_truncated: r.truncated,
        });
        setLastProbe({
          corr_id: corrId,
          image_b64: it.image_b64,
          prompt: r.prompt_used,
          answer: r.answer,
          standalone: r.standalone,
          confirmed: r.standalone ? null : r.confirmed,
          truncated: r.truncated,
        });
        setShowPrompt(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "ask failed";
        patch(corrId, {
          verifying: false,
          manual_answer: msg,
          manual_confirmed: null,
          manual_standalone: promptRef.current.trim().length > 0,
          manual_truncated: false,
        });
        setLastProbe({
          corr_id: corrId,
          image_b64: it.image_b64,
          prompt: promptRef.current.trim(),
          answer: msg,
          standalone: promptRef.current.trim().length > 0,
          confirmed: null,
          truncated: false,
        });
        setShowPrompt(true);
      }
    },
    [patch],
  );

  // Open the socket only while active; the cleanup tears it down on
  // pause / unmount so nothing runs in the background.
  useEffect(() => {
    if (!active) {
      setConn("closed");
      return;
    }
    let stopped = false;
    let reconnectTimer: number | null = null;

    const connect = () => {
      if (stopped) return;
      setConn("connecting");
      const ws = new WebSocket(wsUrl(captionFeedPath(scopeParams)));
      wsRef.current = ws;
      ws.onopen = () => setConn("live");
      ws.onmessage = (e) => {
        let msg: { type: string; items?: FeedEvent[] };
        try {
          msg = JSON.parse(e.data);
        } catch {
          return;
        }
        if (msg.type !== "batch" || !msg.items || msg.items.length === 0) return;
        const evs = msg.items;
        for (const ev of evs) applyEvent(ev);
        setTotal((t) => t + evs.length);
        flush();
      };
      ws.onclose = () => {
        if (stopped) return;
        setConn("closed");
        reconnectTimer = window.setTimeout(connect, RECONNECT_MS);
      };
      ws.onerror = () => ws.close();
    };
    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [active, applyEvent, flush, scopeKey, scopeParams]);

  const clear = () => {
    mapRef.current.clear();
    orderRef.current = [];
    setItems([]);
    setLastProbe(null);
  };

  // Prefill the box with the configured prompt so a dev can tweak from it.
  const loadDefault = async () => {
    setLoadingDefault(true);
    try {
      const cfg = (await api.config.get()) as { vllm?: { weapon_verify_prompt?: unknown } };
      const p = cfg.vllm?.weapon_verify_prompt;
      setCustomPrompt(typeof p === "string" ? p : "");
    } catch {
      /* ignore — leave the box as-is */
    } finally {
      setLoadingDefault(false);
    }
  };

  const promptActive = customPrompt.trim().length > 0;
  const showStoreOnCard = scope.kind !== "store";
  const scopedCamCount = camerasQ.data?.length;

  const statusLabel = paused
    ? "paused"
    : conn === "live"
      ? "live"
      : conn === "connecting"
        ? "connecting…"
        : "disconnected";
  const statusTone =
    statusLabel === "live"
      ? "bg-brand-pink/15 text-brand-pink"
      : statusLabel === "connecting…"
        ? "bg-amber-500/15 text-amber-300"
        : statusLabel === "paused"
          ? "bg-white/10 text-bf-muted"
          : "bg-red-500/15 text-red-300";

  const visibleItems = useMemo(() => {
    if (scope.kind === "all" || !camerasQ.data) return items;
    const allowed = new Set(camerasQ.data.map((c) => c.id));
    return items.filter((it) => !it.camera_id || allowed.has(it.camera_id));
  }, [items, scope.kind, camerasQ.data]);

  const listEndRef = useRef<HTMLDivElement>(null);
  const lastVisibleId = visibleItems[visibleItems.length - 1]?.corr_id;
  useEffect(() => {
    const end = listEndRef.current;
    if (!end || !lastVisibleId) return;
    const scroller = end.closest(".overflow-auto");
    if (!(scroller instanceof HTMLElement)) return;
    const fromBottom =
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    if (fromBottom <= 96) end.scrollIntoView({ block: "end" });
  }, [lastVisibleId]);

  const viewerHeading = viewer
    ? cameraHeading(viewer.camera_id, camById)
    : null;

  const emptyHint = paused
    ? "Paused — feed stopped."
    : conn === "live"
      ? scope.kind !== "all" && scopedCamCount === 0
        ? `No cameras in ${scopeLabel}.`
        : `Waiting for crops from ${scopeLabel}…`
      : "Connecting to the live feed…";

  return (
    <div className="flex h-full flex-col gap-4">
      <PageHeader
        title="Live Captions"
        icon={ScanText}
        description="Watch the descriptions written as people are detected."
        actions={
          <div className="flex items-center gap-2">
            <span
              className={
                "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium " +
                statusTone
              }
            >
              {statusLabel === "live" ? <Wifi size={13} /> : <WifiOff size={13} />}
              {statusLabel}
            </span>
            <span className="rounded-full bg-white/5 px-2.5 py-1 text-xs tabular-nums text-bf-muted">
              {items.length} shown · {total} received
            </span>
            <button
              type="button"
              onClick={() => setShowPrompt((v) => !v)}
              className={
                "flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition hover:bg-white/5 " +
                (promptActive
                  ? "border-bf-violet/50 bg-bf-violet/15 text-white"
                  : "border-white/10 text-white/80")
              }
              title="Prompt sent with the crop"
            >
              <MessageSquareText size={13} />
              Crop prompt{promptActive ? " •" : ""}
            </button>
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1 text-xs font-medium text-white/80 transition hover:bg-white/5"
            >
              {paused ? <Play size={13} /> : <Pause size={13} />}
              {paused ? "Resume" : "Pause"}
            </button>
            <button
              type="button"
              onClick={clear}
              className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1 text-xs font-medium text-white/80 transition hover:bg-white/5"
            >
              <Trash2 size={13} /> Clear
            </button>
          </div>
        }
      />

      {showPrompt && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-3">
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-xs font-medium text-white/90">
              Crop prompt{" "}
              <span className="font-normal text-bf-muted">
                — sent with the image · blank uses the configured weapon-verify prompt
              </span>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={loadDefault}
                disabled={loadingDefault}
                className="flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-white/80 transition hover:bg-white/5 disabled:opacity-40"
              >
                {loadingDefault ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <RotateCcw size={11} />
                )}
                Load configured
              </button>
              <button
                type="button"
                onClick={() => setCustomPrompt("")}
                disabled={!customPrompt}
                className="rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-white/80 transition hover:bg-white/5 disabled:opacity-40"
              >
                Clear
              </button>
            </div>
          </div>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            rows={4}
            spellCheck={false}
            placeholder="What is this person holding?"
            className="w-full resize-y rounded-lg border border-white/10 bg-black/30 p-2 font-mono text-xs text-white/90 outline-none placeholder:text-white/25 focus:border-bf-violet/50"
          />
          <p className="mt-1 text-[11px] text-bf-muted">
            {promptActive
              ? "Ask on a crop sends this text with the image."
              : "Force 2nd pass uses the configured weapon-verify prompt."}
          </p>
        </div>
      )}

      {lastProbe && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-3">
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <h2 className="text-xs font-medium text-white/90">vLLM answer</h2>
            <span className="truncate font-mono text-[10px] text-bf-muted" title={lastProbe.corr_id}>
              {lastProbe.corr_id.slice(0, 8)}
            </span>
          </div>
          <div className="flex gap-3">
            {lastProbe.image_b64 ? (
              <img
                src={`data:image/jpeg;base64,${lastProbe.image_b64}`}
                alt={`crop ${lastProbe.corr_id}`}
                className="h-36 w-28 shrink-0 rounded-lg bg-black/40 object-contain"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              {lastProbe.prompt ? (
                <p className="mb-2 whitespace-pre-wrap break-words font-mono text-[11px] text-white/45">
                  {lastProbe.prompt}
                </p>
              ) : null}
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-white/90">
                {lastProbe.answer}
              </p>
              {lastProbe.truncated ? (
                <p className="mt-2 text-[11px] text-amber-300/80">
                  Cut off at the token cap.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {visibleItems.length === 0 ? (
        <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-white/10 text-sm text-bf-muted">
          {emptyHint}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
            {visibleItems.map((it) => {
              const heading = cameraHeading(it.camera_id, camById);
              return (
                <CaptionCard
                  key={it.corr_id}
                  item={it}
                  onVerify={verify}
                  onOpen={setViewer}
                  cameraName={heading.name}
                  storeName={showStoreOnCard ? heading.store : undefined}
                  askMode={promptActive}
                />
              );
            })}
          </div>
          <div ref={listEndRef} aria-hidden />
        </>
      )}

      {viewer && viewerHeading && (
        <ZoomableStillDialog
          title={viewerHeading.name}
          subtitle={
            <>
              {showStoreOnCard && viewerHeading.store
                ? `${viewerHeading.store} · `
                : null}
              trk {viewer.track_id ?? "?"}
              {viewer.acquired_at_utc
                ? ` · ${new Date(viewer.acquired_at_utc).toLocaleTimeString()}`
                : ""}
            </>
          }
          cropSrc={jpegSrc(viewer.image_b64)}
          frameSrc={jpegSrc(viewer.frame_b64)}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
